(() => {
  // app/scripts/utils/debounceTime.js
  function debounceTime(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  // app/scripts/utils/disableScroll.js
  function disableScroll() {
    document.body.classList.add("overflow-hidden");
    document.documentElement.style.paddingRight = "var(--scrollbar-width)";
  }

  // app/scripts/utils/enableScroll.js
  function enableScroll() {
    setTimeout(() => {
      document.body.classList.remove("overflow-hidden");
      document.documentElement.style.paddingRight = "";
    }, 50);
  }

  // app/scripts/popup-select-joblist.js
  var LIMIT = 10;
  var PopupSelectJobList = class extends HTMLElement {
    constructor() {
      super();
      this.resolve = null;
      this.firstConnected = false;
      this.searchInput = this.querySelector(".js-search-input");
      this.hiddenSelect = this.querySelector(".js-select");
      this.inputElm = this.querySelector(".js-input");
      this.resetBtn = this.querySelector("[type='reset']");
      this.form = this.querySelector("form");
      this.dropdownList = this.querySelector(".js-dropdown-list");
      this.dropdownEmpty = this.querySelector(".js-dropdown-empty");
      this.dropdownLoader = this.querySelector(".js-dropdown-loader");
      this.dropdownSentinel = this.querySelector(".js-dropdown-sentinel");
      this.currentPage = 0;
      this.totalPages = 0;
      this.isLoading = false;
      this.isDropdownOpen = false;
      this.items = [];
      this.searchId = 0;
    }
    connectedCallback() {
      if (this.firstConnected)
        return;
      this.firstConnected = true;
      this.initCloseButtons();
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
      this.searchInput.addEventListener("focus", () => this.openDropdown());
      this.searchInput.addEventListener("input", debounceTime(() => {
        this.resetDropdownItems();
        this.loadPage(1);
        this.inputElm.value = "";
      }, 500));
      this.inputElm.addEventListener("input", () => {
        this.searchInput.value = "";
        this.clearSelection();
        this.closeDropdown();
      });
      this._onDocClick = (e) => {
        if (!this.querySelector("[data-dropdown]").contains(e.target)) {
          this.closeDropdown();
        }
      };
      document.addEventListener("click", this._onDocClick);
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.isLoading && this.currentPage < this.totalPages) {
            this.loadPage(this.currentPage + 1);
          }
        },
        { root: this.dropdownList, threshold: 0 }
      );
      this.scrollObserver.observe(this.dropdownSentinel);
    }
    disconnectedCallback() {
      document.removeEventListener("click", this._onDocClick);
      this.scrollObserver?.disconnect();
    }
    openDropdown() {
      if (this.isDropdownOpen)
        return;
      this.isDropdownOpen = true;
      this.dropdownList.classList.remove("hidden");
      this.scrollObserver.unobserve(this.dropdownSentinel);
      this.scrollObserver.observe(this.dropdownSentinel);
    }
    closeDropdown() {
      this.isDropdownOpen = false;
      this.dropdownList.classList.add("hidden");
    }
    resetDropdownItems() {
      this.dropdownList.querySelectorAll("[data-job-list-item]").forEach((el) => el.remove());
      this.dropdownList.scrollTop = 0;
      this.currentPage = 0;
      this.totalPages = 0;
      this.clearSelection();
    }
    clearSelection() {
      this.hiddenSelect.value = "";
    }
    async loadPage(page) {
      this.isLoading = true;
      this.dropdownLoader.classList.remove("hidden");
      this.dropdownEmpty.classList.add("hidden");
      const currentSearchId = ++this.searchId;
      try {
        const response = await JobLists.fetchJobLists({
          search: this.searchInput.value.trim(),
          page,
          limit: LIMIT
        });
        if (currentSearchId !== this.searchId)
          return;
        this.currentPage = response.page;
        this.totalPages = response.totalPages;
        const items = response.items;
        if (items.length === 0 && page === 1) {
          this.dropdownEmpty.classList.remove("hidden");
        }
        items.forEach((jobList) => {
          const li = document.createElement("li");
          li.dataset.jobListItem = "";
          li.dataset.value = jobList.id;
          li.textContent = jobList.name;
          li.className = "py-2 px-4 cursor-pointer hover:bg-dark-neutral-20 transition-colors";
          if (this.items.length === 1) {
            const itemToAdd = this.items[0];
            const isAdded = jobList.items.some((item) => item.variantGid === itemToAdd.variantGid);
            if (isAdded) {
              li.classList.add("opacity-50", "pointer-events-none");
              li.setAttribute("aria-disabled", "true");
            }
          }
          li.addEventListener("click", () => this.selectItem(jobList));
          this.dropdownList.insertBefore(li, this.dropdownLoader);
        });
      } catch (error) {
        console.error("Failed to load job lists:", error);
      } finally {
        if (currentSearchId === this.searchId) {
          this.isLoading = false;
          this.dropdownLoader.classList.add("hidden");
        }
      }
    }
    selectItem(jobList) {
      this.hiddenSelect.value = jobList.id;
      this.searchInput.value = jobList.name;
      this.inputElm.value = "";
      this.closeDropdown();
    }
    async handleSubmit(e) {
      e.preventDefault();
      if (!this.inputElm.value.trim() && !this.hiddenSelect.value)
        return;
      try {
        this.classList.add("is-loading");
        const selectedJobList = this.inputElm.value.trim() ? await JobLists.create({
          name: this.inputElm.value.trim(),
          description: "",
          customerGid: theme.customer.gid,
          items: [],
          companyGid: theme.customer.company.gid
        }) : await JobLists.getById(this.hiddenSelect.value);
        const filteredItems = this.items.filter(
          ({ variantGid }) => !selectedJobList.items.some((item) => item.variantGid === variantGid)
        );
        const itemsToUpdate = [
          ...filteredItems.map(({ handle, variantGid, quantity = 1, productGid }) => ({
            handle,
            variantGid,
            productGid,
            quantity,
            note: "",
            createdAt: (/* @__PURE__ */ new Date()).toISOString(),
            updatedAt: (/* @__PURE__ */ new Date()).toISOString()
          }))
        ];
        const dataToUpdate = {
          customerGid: selectedJobList.customerGid,
          name: selectedJobList.name,
          description: selectedJobList.description,
          items: [...itemsToUpdate, ...selectedJobList.items]
        };
        await JobLists.update(selectedJobList.id, dataToUpdate);
        this.resolve(selectedJobList);
      } catch (error) {
        throw error;
      } finally {
        this.close(true);
      }
    }
    initCloseButtons() {
      this.querySelectorAll(".js-close-btn").forEach((button) => {
        button.addEventListener("click", () => this.close());
      });
    }
    close(force = false) {
      if (this.classList.contains("is-loading") && !force)
        return;
      this.classList.remove("is-open");
      enableScroll();
      if (this.resolve) {
        this.resolve(false);
        this.resolve = null;
      }
      this.reset();
    }
    open() {
      this.classList.add("is-open");
      disableScroll();
      this.loadPage(1);
    }
    async select(items = []) {
      this.items = [...items];
      this.open();
      return new Promise((resolve) => {
        this.resolve = resolve;
      });
    }
    reset() {
      this.resetBtn.click();
      this.resolve = null;
      this.items = [];
      this.classList.remove("is-loading");
      this.searchInput.value = "";
      this.resetDropdownItems();
      this.closeDropdown();
    }
  };
  customElements.define("popup-select-joblist", PopupSelectJobList);
})();
